import { z } from 'zod';

// Type definitions for form fields
export interface FormField {
  name: string;
  db_name: string;
  field: string;
  options: string | number[] | any[];
  unit: string;
  required: boolean;
  prc: string;
  component: string;
  value: any;
  isPercent?: boolean;
}

// Type definitions for form sections
export interface FormSection {
  [sectionName: string]: FormField[];
}

// Options for schema generation
export interface SchemaGenerationOptions {
  selectedTech?: string | null;
  excludeSections?: string[];
  fieldOverrides?: { [fieldName: string]: z.ZodType<any> };
}

/**
 * Generates a Zod schema from any form definition
 * @param formData - The form definition object
 * @param options - Optional configuration for schema generation
 * @returns The generated Zod schema
 */
export const generateFormSchema = (formData: FormSection, options: SchemaGenerationOptions = {}): z.ZodObject<any> => {
  const { selectedTech = null, excludeSections = [], fieldOverrides = {} } = options;
  const schemaFields: { [key: string]: z.ZodType<any> } = {};

  // Helper function to create Zod validation for number fields
  const createNumberValidation = (field: FormField): z.ZodType<any> => {
    const { options, required, isPercent } = field;

    if (required) {
      // For required fields, use a more robust validation approach
      let validation: any = z
        .union([z.string(), z.number()])
        .refine(val => {
          // First check if empty
          if (val === '' || val === null || val === undefined) {
            return false;
          }
          // Then check if it's a valid number
          const num = typeof val === 'string' ? parseFloat(val) : val;
          return !isNaN(num) && isFinite(num);
        }, 'Required')
        .transform(val => {
          return typeof val === 'string' ? parseFloat(val) : val;
        });

      if (Array.isArray(options) && options.length >= 2) {
        const [min, max] = options;

        if (min !== undefined && min !== null) {
          validation = validation.refine((val: number) => val >= min, `${field.name} must be at least ${min}`);
        }
        if (max !== undefined && max !== null) {
          validation = validation.refine((val: number) => val <= max, `${field.name} cannot exceed ${max}`);
        }
      }

      // Handle percentage fields
      if (isPercent) {
        validation = validation
          .refine((val: number) => val >= 0, `${field.name} cannot be negative`)
          .refine((val: number) => val <= 100, `${field.name} cannot exceed 100`);
      }

      return validation as z.ZodType<any>;
    } else {
      // For optional fields, use the original coerce logic
      let validation: z.ZodNumber = z.coerce.number();

      if (Array.isArray(options) && options.length >= 2) {
        const [min, max] = options;

        if (min !== undefined && min !== null) {
          validation = validation.min(min, `${field.name} must be at least ${min}`);
        }
        if (max !== undefined && max !== null) {
          validation = validation.max(max, `${field.name} cannot exceed ${max}`);
        }
      }

      // Handle percentage fields
      if (isPercent) {
        validation = validation.min(0, `${field.name} cannot be negative`).max(100, `${field.name} cannot exceed 100`);
      }

      return z.union([validation, z.literal(''), z.null(), z.undefined()]).optional();
    }
  };

  // Helper function to create Zod validation for string fields
  const createStringValidation = (field: FormField): z.ZodType<any> => {
    const { required } = field;
    let validation: z.ZodString = z.string();

    if (required) {
      validation = validation.min(1, 'Required');
    }

    // Add max length validation
    validation = validation.max(250, `${field.name} cannot exceed 250 characters`);

    // Make optional if not required
    if (!required) {
      return z.union([validation, z.literal(''), z.null(), z.undefined()]).optional();
    }

    return validation;
  };

  // Helper function to create Zod validation for select fields
  const createSelectValidation = (field: FormField): z.ZodType<any> => {
    const { required } = field;
    let validation: z.ZodString = z.string();

    if (required) {
      validation = validation.min(1, 'Required');
    } else {
      return z.union([validation, z.literal(''), z.null(), z.undefined()]).optional();
    }

    return validation;
  };

  // Iterate through all sections and fields
  Object.entries(formData).forEach(([sectionName, section]) => {
    // Skip excluded sections
    if (excludeSections.includes(sectionName)) {
      return;
    }

    if (Array.isArray(section)) {
      section.forEach(field => {
        const { db_name, field: fieldType, required, prc } = field;

        // Skip fields that are technology-specific but don't match current tech
        if (prc && selectedTech && prc !== selectedTech) {
          return;
        }

        // Skip fields for technologies that aren't selected (material-specific logic)
        if (selectedTech) {
          if (
            sectionName === 'Additive Manufacturing' &&
            selectedTech !== 'am' &&
            selectedTech !== 'additive_manufacturing'
          ) {
            return;
          }
          if (sectionName === 'Casting' && selectedTech !== 'casting') {
            return;
          }
          if (sectionName === 'Milling' && selectedTech !== 'milling') {
            return;
          }
        }

        // Check for field overrides first
        if (fieldOverrides[db_name]) {
          schemaFields[db_name] = fieldOverrides[db_name];
          return;
        }

        // Generate validation based on field type
        switch (fieldType) {
          case 'number':
            schemaFields[db_name] = createNumberValidation(field);
            break;
          case 'text':
            schemaFields[db_name] = createStringValidation(field);
            break;
          case 'select':
            schemaFields[db_name] = createSelectValidation(field);
            break;
          default:
            // For other field types (like SparkMultipleSelection), make them optional
            if (!required) {
              schemaFields[db_name] = z.any().optional();
            } else {
              schemaFields[db_name] = z.any();
            }
            break;
        }
      });
    }
  });

  return z.object(schemaFields);
};
